import { useState } from "react";

import { AddEmployeeProtectMedicalNotePopup } from "@/Common/Components/AddEmployeePopup/AddEmployeeProtectMedicalNotePopup";
import { EmployeeListComponent } from "@/Common/Components/AddEmployeePopup/EmployeeListComponent";
import { RadioButtonv2 } from "@/Common/Components/Inputs/RadioButtonv2";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { Guid } from "@/Common/Models/Guid";
import { AllowlistType } from "@/Common/Models/UI/Enums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";

import styles from "@/Administration/Components/Users/Allowlist/ProtectMedicalNoteComponent.module.scss";

enum PatientTabOptionsEnum {SpecificUsers, NoRestriction}

interface ProtectMedicalNoteComponentProps {
    onChange: (model: IAllowlistModel) => void
    model: IAllowlistModel;
    modelChanges?: IAllowlistModel;
    editable: boolean;
    dataCy?: string;
}

function ProtectMedicalNoteComponent(props: ProtectMedicalNoteComponentProps): JSX.Element {
    const [showAddEmployeesPopup, setShowAddEmployeesPopup] = useState(false);
    const { getText } = useTranslation();

    const onRemoveClick = (contactFacetId: Guid): void => {
        const nextMembers = (props.modelChanges?.MemberIds ?? props.model.MemberIds).filter((employeeId => employeeId != contactFacetId));
        onPartialChange({ MemberIds: nextMembers });
    };

    const onRemoveAllClick = (): void => {
        onPartialChange({ MemberIds: [] });
    };

    const onIsActiveChange = (isActive: boolean): void => {
        onPartialChange({ IsActive: props.model.IsActive !== isActive ? isActive : null });
    };

    const onMemberChange = (memberIds: Guid[]): void => {
        const nextMembers = [...(props.modelChanges?.MemberIds ?? props.model.MemberIds), ...memberIds];
        const orderedNextMembers = Array.from(nextMembers).sort((a, b) => a.localeCompare(b));
        const orderedMembers = Array.from(props.model.MemberIds).sort((a, b) => a.localeCompare(b));
        const membersHaveChanged = JSON.stringify(orderedNextMembers) !== JSON.stringify(orderedMembers);
        onPartialChange({ MemberIds: membersHaveChanged ? nextMembers : null });
    };

    const onPartialChange = (change: Partial<IAllowlistModel>): void => {
        const base = props.modelChanges ?? {
            EmployeeId: props.model.EmployeeId,
            AllowlistType: AllowlistType.MedicalNoteAllowlist,
            MemberIds: null,
            IsActive: null
        };

        props.onChange({ ...base, ...change });
    };

    return (
        <>
            {showAddEmployeesPopup && (
                <AddEmployeeProtectMedicalNotePopup
                    contactFacetIds={props.modelChanges?.MemberIds ?? props.model.MemberIds}
                    onSave={contactFacetIds => {
                        setShowAddEmployeesPopup(false);
                        onMemberChange(contactFacetIds);
                    }}
                    onClose={() => setShowAddEmployeesPopup(false)}
                    dataCy={props.dataCy}
                />
            )}
            <div key={props.dataCy} data-cy={props.dataCy} className={styles.medicalNoteAllowlistComponent}>
                <div className={styles.medicalNoteAllowlistRadioButtonsContainer}>
                    <RadioButtonv2
                        title={`${getText("Allowlist_TitleLabel")}:`}
                        htmlName="PatientOptionsRadioButtons"
                        items={[
                            new KeyValuePair(PatientTabOptionsEnum.NoRestriction, getText("Allowlist_NoRestrictionLabel")),
                            new KeyValuePair(PatientTabOptionsEnum.SpecificUsers, getText("Allowlist_SpecificUsersLabel"))
                        ].toImmutable()}
                        selectedItem={(props.modelChanges?.IsActive ?? props.model?.IsActive) ? PatientTabOptionsEnum.SpecificUsers : PatientTabOptionsEnum.NoRestriction}
                        onChangeCallback={selectedItem => onIsActiveChange(selectedItem.Key === PatientTabOptionsEnum.SpecificUsers)}
                        disabled={!props.editable}
                        dataCy={props.dataCy}
                    />
                    {(props.modelChanges?.IsActive ?? props.model.IsActive) && props.editable && (
                        <a data-cy={`${props.dataCy}-add-user-link`} onClick={() => setShowAddEmployeesPopup(true)}>[+] {getText("Allowlist_AddAUserLabel")}</a>
                    )}
                </div>

                {((props.modelChanges?.IsActive ?? props.model.IsActive) && (props.modelChanges?.MemberIds ?? props.model.MemberIds)?.any()) && (
                    <div className={styles.employeeListContainer}>
                        <div className={styles.employeeListTextDescription}>
                            {getText("Allowlist_MedicalNoteAllowlistWarning")}
                        </div>
                        <div className={styles.employeeListHeader}>
                            <h4 className="thin-font">{getText("Allowlist_IndividualUsersLabel")}</h4>
                            <span
                                hidden={!props.editable}
                                data-cy={`${props.dataCy}-remove-all-users`}
                                className={styles.removeAllUsersButton}
                                onClick={() => onRemoveAllClick()}
                            >
                                {getText("Allowlist_RemoveAllUsersLabel")}
                            </span>
                        </div>
                        <EmployeeListComponent
                            onRemoveEmployeeClick={userToRemove => onRemoveClick(userToRemove.ContactFacetId)}
                            employees={(props.modelChanges?.MemberIds ?? props.model.MemberIds).map(contactFacetId => cacheStore.GetClinicians(true).find(employee => employee.ContactFacetId === contactFacetId))}
                            showDeleteIcons={props.editable}
                            dataCy={props.dataCy}
                        />
                    </div>
                )}
            </div>
        </>
    );
}

export { PatientTabOptionsEnum, ProtectMedicalNoteComponent };
